import { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles, Upload, FileText, ChevronDown, ChevronRight,
  CheckCircle2, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { DocumentChecklistInline } from "@/components/DocumentChecklistCard";

/* Smart Sort — Phase 2
 * -------------------
 * User drops one big multi-page PDF (or single image). Claude classifies each
 * page, groups consecutive pages into logical documents, and assigns each doc
 * to a person on the case. User reviews thumbnails + assignments, corrects any
 * misclassifications, and clicks "Apply all".
 */

const PERSON_LABEL = {
  petitioner: "Petitioner",
  beneficiary: "Beneficiary",
  petitioner_father: "Petitioner's father",
  petitioner_mother: "Petitioner's mother",
  beneficiary_father: "Beneficiary's father",
  beneficiary_mother: "Beneficiary's mother",
  new_child: "New child (auto-add)",
  unknown: "— skip this doc —",
};

const DOC_TYPE_OPTIONS = [
  { id: "petitioner_id", label: "Petitioner ID / passport / green card" },
  { id: "beneficiary_passport", label: "Beneficiary passport" },
  { id: "beneficiary_birth_cert", label: "Beneficiary birth certificate" },
  { id: "child_birth_cert", label: "Child birth certificate" },
  { id: "marriage_cert", label: "Marriage certificate" },
  { id: "tax_return", label: "Tax return" },
  { id: "paystub", label: "Paystub" },
  { id: "bank_statement", label: "Bank statement" },
  { id: "other", label: "Other" },
];

export default function SmartSort({ caseId, caseData, roster, onDone }) {
  const inputRef = useRef(null);
  const [state, setState] = useState({ phase: "idle" });
  // phase: idle | uploading | review | applying | done

  const upload = useCallback(async (file) => {
    if (!file) return;
    setState({ phase: "uploading", filename: file.name });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/cases/${caseId}/bulk-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180_000 },
      );
      setState({
        phase: "review",
        stagingId: data.staging_id,
        pages: data.pages_b64,
        docs: data.documents.map((d, i) => ({ ...d, _uid: `d${i}` })),
        filename: file.name,
      });
    } catch (err) {
      console.error("[SmartSort] upload failed:", err?.message || err);
      const msg = friendlyError(err, "Smart Sort couldn't read that file.");
      toast.error(msg);
      setState({ phase: "idle", error: msg });
    }
  }, [caseId]);

  const confirm = useCallback(async () => {
    setState((s) => ({ ...s, phase: "applying" }));
    try {
      const { data } = await api.post(`/cases/${caseId}/bulk-confirm`, {
        staging_id: state.stagingId,
        documents: state.docs.map(({ _uid, ...d }) => d),
      });
      toast.success(`${data.persisted.length} document${data.persisted.length === 1 ? "" : "s"} applied to your case.`);
      setState({ phase: "done", persisted: data.persisted });
      await onDone?.();
    } catch (err) {
      console.error("[SmartSort] confirm failed:", err?.message || err);
      const msg = friendlyError(err, "We couldn't apply these documents. Try again.");
      toast.error(msg);
      setState((s) => ({ ...s, phase: "review", error: msg }));
    }
  }, [caseId, state.stagingId, state.docs, onDone]);

  const restart = () => setState({ phase: "idle" });

  const rosterOptions = buildRosterOptions(roster || [], caseData);

  if (state.phase === "review") {
    return (
      <ReviewPanel
        state={state}
        setState={setState}
        rosterOptions={rosterOptions}
        onConfirm={confirm}
        onCancel={restart}
      />
    );
  }
  if (state.phase === "applying") {
    return <ApplyingPanel />;
  }
  if (state.phase === "done") {
    return <DonePanel persisted={state.persisted} onAnother={restart} />;
  }

  return (
    <div className="card-surface p-6" data-testid="smart-sort-card">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl" style={{ background: "var(--ai-bg)" }}>
          <Sparkles className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg leading-tight" style={{ color: "var(--text-primary)" }}>
            Smart Sort — upload everything at once
          </h3>
          <p className="text-sm mt-1 mb-4" style={{ color: "var(--text-secondary)" }}>
            Drop one PDF with all your documents (passports, birth certificates, marriage
            cert, tax returns, etc.). We'll read each page, figure out
            what it is, and assign it to the right person on this case. You review the
            results and click Apply.
          </p>
          <div
            className="dashed-drop p-8 text-center cursor-pointer"
            data-testid="smart-sort-drop"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) upload(f);
            }}
          >
            <input
              ref={inputRef}
              data-testid="smart-sort-input"
              type="file"
              className="hidden"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            {state.phase === "uploading" ? (
              <UploadingIndicator filename={state.filename} />
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--action)" }} />
                <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
                  Drop a PDF or photo with all your documents
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                  Multi-page PDFs up to 20 MB · we&apos;ll separate each document for you
                </div>
              </>
            )}
          </div>
          {state.error && (
            <div className="mt-3 p-3 rounded-md text-xs flex items-start gap-2"
                 style={{ background: "#FEF2F2", color: "#991B1B" }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{state.error}</span>
            </div>
          )}
          <DocumentChecklistInline />
        </div>
      </div>
    </div>
  );
}

function buildRosterOptions(roster, caseData) {
  const opts = [];
  const seen = new Set();
  for (const row of roster) {
    if (seen.has(row.person_ref)) continue;
    seen.add(row.person_ref);
    opts.push({ value: row.person_ref, label: row.display_name || PERSON_LABEL[row.person_ref] || row.person_ref });
  }
  // Include potential parents (even if not yet on roster)
  for (const pr of ["petitioner_father", "petitioner_mother", "beneficiary_father", "beneficiary_mother"]) {
    if (!seen.has(pr)) {
      opts.push({ value: pr, label: PERSON_LABEL[pr] });
      seen.add(pr);
    }
  }
  opts.push({ value: "new_child", label: PERSON_LABEL.new_child });
  opts.push({ value: "unknown", label: PERSON_LABEL.unknown });
  return opts;
}

function UploadingIndicator({ filename }) {
  const messages = [
    "Reading each page…",
    "Recognizing document types…",
    "Grouping pages into documents…",
    "Assigning documents to the right people…",
    "Extracting names, dates, and places…",
    "Almost there — finalizing…",
  ];
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 3200);
    return () => clearInterval(t);
  }, [messages.length]);

  return (
    <div className="py-2" data-testid="smart-sort-uploading">
      <div className="dot-typing mb-2">
        <span></span><span></span><span></span>
      </div>
      <div className="font-semibold" style={{ color: "var(--text-primary)" }}>
        {messages[msgIdx]}
      </div>
      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
        Analyzing <span className="font-mono">{filename}</span> · usually takes 10–30 seconds.
      </div>
    </div>
  );
}

function ApplyingPanel() {
  const messages = [
    "Applying documents to the case…",
    "Filling DS-260 fields…",
    "Filling family-member fields…",
    "Cross-checking dates and names…",
  ];
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 2200);
    return () => clearInterval(t);
  }, [messages.length]);
  return (
    <div className="card-surface p-6 text-center" data-testid="smart-sort-applying">
      <div className="dot-typing mb-3"><span></span><span></span><span></span></div>
      <div className="font-semibold">{messages[msgIdx]}</div>
    </div>
  );
}

function DonePanel({ persisted, onAnother }) {
  return (
    <div className="card-surface p-6 anim-in" data-testid="smart-sort-done">
      <div className="flex items-start gap-3 mb-4">
        <CheckCircle2 className="w-6 h-6 flex-shrink-0" style={{ color: "#065F46" }} />
        <div>
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Documents applied
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            {persisted.length} document{persisted.length === 1 ? "" : "s"} sorted and applied to your case.
            Your family's DS-260 answers are now pre-populated with the extracted data.
          </p>
        </div>
      </div>
      <ul className="text-xs space-y-1 mb-4">
        {persisted.map((p) => (
          <li key={p.id} className="flex items-center gap-2">
            <CheckCircle2 className="w-3 h-3" style={{ color: "#065F46" }} />
            <span>{p.doc_type.replace(/_/g, " ")} → {p.person_ref}</span>
            {p.applied_updates.length > 0 && (
              <span style={{ color: "var(--text-secondary)" }}>
                (filled: {p.applied_updates.join(", ")})
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="flex gap-2 items-center">
        <a
          href="#review-stage"
          onClick={(e) => {
            e.preventDefault();
            // Navigate to review stage in the same case
            window.location.hash = "review";
            // Dispatch a custom event our stage router can listen to
            window.dispatchEvent(new CustomEvent("gcu:goto-stage", { detail: "review" }));
          }}
          className="btn-primary text-xs py-1.5"
          data-testid="smart-sort-goto-review"
        >
          Review your DS-260 →
        </a>
        <button
          className="btn-ghost text-xs"
          onClick={onAnother}
          data-testid="smart-sort-upload-more"
        >
          Upload another stack
        </button>
      </div>
    </div>
  );
}

/* ---------------- REVIEW PANEL ---------------- */

function ReviewPanel({ state, setState, rosterOptions, onConfirm, onCancel }) {
  const updateDoc = (uid, patch) => {
    setState((s) => ({
      ...s,
      docs: s.docs.map((d) => (d._uid === uid ? { ...d, ...patch } : d)),
    }));
  };
  const removeDoc = (uid) => {
    setState((s) => ({ ...s, docs: s.docs.filter((d) => d._uid !== uid) }));
  };

  const validDocs = state.docs.filter((d) => d.person_ref !== "unknown");
  const skippedCount = state.docs.length - validDocs.length;

  return (
    <div className="card-surface p-6 anim-in" data-testid="smart-sort-review">
      <div className="flex items-start gap-3 mb-5">
        <Sparkles className="w-6 h-6 flex-shrink-0" style={{ color: "var(--ai-text)" }} />
        <div className="flex-1">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Detected {state.docs.length} document{state.docs.length === 1 ? "" : "s"} in {state.filename}
          </h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
            Review each one. If Claude got a classification wrong, use the dropdowns to fix
            the person or document type. Skip anything you don&apos;t want applied.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {state.docs.map((doc) => (
          <DocReviewCard
            key={doc._uid}
            doc={doc}
            pages={state.pages}
            rosterOptions={rosterOptions}
            onUpdate={(patch) => updateDoc(doc._uid, patch)}
            onRemove={() => removeDoc(doc._uid)}
          />
        ))}
      </div>

      {state.error && (
        <div className="mt-4 p-3 rounded-md text-xs" style={{ background: "#FEF2F2", color: "#991B1B" }}>
          {state.error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between border-t pt-4"
           style={{ borderColor: "var(--border-stone)" }}>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {validDocs.length} will apply
          {skippedCount > 0 && ` · ${skippedCount} skipped`}
        </div>
        <div className="flex gap-2">
          <button
            className="btn-ghost text-sm"
            onClick={onCancel}
            data-testid="smart-sort-cancel"
          >
            Start over
          </button>
          <button
            className="btn-primary text-sm"
            onClick={onConfirm}
            disabled={validDocs.length === 0}
            data-testid="smart-sort-confirm"
          >
            Apply {validDocs.length} document{validDocs.length === 1 ? "" : "s"} to case
          </button>
        </div>
      </div>
    </div>
  );
}

function DocReviewCard({ doc, pages, rosterOptions, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const thumb = pages[doc.page_indices[0]];
  const pageCount = doc.page_indices.length;
  const displayFields = Object.entries(doc.extracted_fields || {}).filter(([, v]) => v);

  const confidenceColor = confidenceStyle(doc.confidence);

  return (
    <div
      className="border rounded-lg overflow-hidden"
      style={{ borderColor: "var(--border-stone)", background: "white" }}
      data-testid={`smart-sort-doc-${doc._uid}`}
    >
      <div className="flex gap-4 p-4">
        <img
          src={`data:image/png;base64,${thumb}`}
          alt="document thumbnail"
          className="w-24 h-32 object-cover rounded border flex-shrink-0"
          style={{ borderColor: "var(--border-stone)" }}
        />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill pill-ai text-[10px]">{doc.doc_type.replace(/_/g, " ")}</span>
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
              {pageCount} page{pageCount === 1 ? "" : "s"} (#{doc.page_indices.map((i) => i + 1).join(", ")})
            </span>
            <span className={`pill text-[10px] ${confidenceColor.pill}`}>
              {Math.round(doc.confidence * 100)}% confident
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <label className="block">
              <span className="block mb-0.5" style={{ color: "var(--text-secondary)" }}>Person</span>
              <select
                className="field-input py-1 px-2 text-xs"
                value={doc.person_ref}
                onChange={(e) => onUpdate({ person_ref: e.target.value })}
                data-testid={`smart-sort-person-${doc._uid}`}
              >
                {rosterOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block mb-0.5" style={{ color: "var(--text-secondary)" }}>Type</span>
              <select
                className="field-input py-1 px-2 text-xs"
                value={doc.doc_type}
                onChange={(e) => onUpdate({ doc_type: e.target.value })}
                data-testid={`smart-sort-doctype-${doc._uid}`}
              >
                {DOC_TYPE_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>
          {doc.notes && (
            <div className="text-[11px] italic" style={{ color: "var(--text-secondary)" }}>
              Note: {doc.notes}
            </div>
          )}
          <div className="flex gap-3 pt-1 text-[11px]">
            <button
              type="button"
              className="font-semibold flex items-center gap-1"
              style={{ color: "var(--action)" }}
              onClick={() => setExpanded((v) => !v)}
              data-testid={`smart-sort-expand-${doc._uid}`}
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} extracted fields ({displayFields.length})
            </button>
            <button
              type="button"
              className="font-semibold flex items-center gap-1"
              style={{ color: "#991B1B" }}
              onClick={onRemove}
              data-testid={`smart-sort-remove-${doc._uid}`}
            >
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      </div>
      {expanded && displayFields.length > 0 && (
        <div className="border-t px-4 py-3 text-xs bg-[color:var(--surface-cream,#FDFBF5)]"
             style={{ borderColor: "var(--border-stone)" }}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            {displayFields.map(([k, v]) => (
              <div key={k} className="contents">
                <dt style={{ color: "var(--text-secondary)" }}>{k.replace(/_/g, " ")}</dt>
                <dd style={{ color: "var(--text-primary)" }}>{String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {expanded && pageCount > 1 && (
        <div className="border-t px-4 py-3 flex gap-2 overflow-x-auto"
             style={{ borderColor: "var(--border-stone)" }}>
          {doc.page_indices.map((i) => (
            <img
              key={i}
              src={`data:image/png;base64,${pages[i]}`}
              alt={`page ${i + 1}`}
              className="w-16 h-20 object-cover rounded border flex-shrink-0"
              style={{ borderColor: "var(--border-stone)" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function confidenceStyle(c) {
  if (c >= 0.85) return { pill: "pill-ok" };
  if (c >= 0.6) return { pill: "pill-warn" };
  return { pill: "pill-bad" };
}

// Export helper icon usage indirectly (avoid unused-import lint noise)
export { FileText };
